import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";

export const SimpleMenu = ({
	id,
	label,
	isRequired,
	options,
	value,
	onChange,
	labelClass,
	fieldClass,
	downImgClass,
	isValid,
	errorMessage,
}) => {
	return (
		<>
			<label htmlFor={id} className={labelClass}>
				<p>
					{label}
					{isRequired ? <span>*</span> : null}
				</p>
			</label>
			<div className={"relative"}>
				<select
					id={id}
					name={id}
					value={value}
					onChange={onChange}
					className={
						"w-full appearance-none rounded-none border-0 border-b border-[#a8a8a8] bg-white bg-none pb-0 pt-1.5 shadow-none focus:outline-none focus:ring-0 " +
						fieldClass
					}
				>
					{options.map((option, index) => (
						<option key={index} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
				<img
					alt={"dropdown-button"}
					className={downImgClass}
					src={window.assetUrl("/images/liff/triangle.png")}
				/>
			</div>
			<p
				className={
					(isValid ? "text-transparent" : "text-[#ff4750]") + " text-[10px]"
				}
			>
				{errorMessage}
			</p>
		</>
	);
};

export const DateMenu = ({
	id,
	label,
	isRequired,
	onChange,
	labelClass,
	fieldClass,
	downImgClass,
	isValid,
	isValidYear,
	isValidMonth,
	isValidDay,
	errorMessage,
}) => {
	const yearSelectRef = useRef(null);
	const monthSelectRef = useRef(null);
	const daySelectRef = useRef(null);
	const [year, setYear] = useState(undefined);
	const [month, setMonth] = useState(undefined);
	const [day, setDay] = useState(undefined);
	const [years, setYears] = useState([]);
	const [months, setMonths] = useState([]);
	const [days, setDays] = useState([]);

	const handleDateDropdownClick = () => {
    const now = dayjs();
		const currentYear = now.year();
		
		if (!year) {
			setYear(currentYear - 18);
		}
		if (!month) {
			setMonth(1);
		}
	};

	const updateDays = (y, m) => {
		const daysInMonth = dayjs(`${y}-${m}`).daysInMonth();
		const daysArray = [];
		for (let i = 1; i <= daysInMonth; i++) {
			daysArray.push(i);
		}
		setDays(daysArray);
	};

	useEffect(() => {
		// 取得 18 ~ 75 歲的年份
		const y = [];
		const currentYear = dayjs().year();
		for (let i = currentYear - 90; i <= currentYear - 18; i++) {
			y.push(i);
		}
		setYears(y);

		// 取得月份
		const m = [];
		for (let i = 1; i <= 12; i++) {
			m.push(i);
		}
		setMonths(m);

		const now = dayjs();
		updateDays(now.year(), now.month());
	}, []);

	useEffect(() => {
		// 如果當前選擇的日期不在當前月份內，則重置日期
		const daysInMonth = dayjs(`${year}-${month}`).daysInMonth();
		if (day > daysInMonth) {
			setDay(daysInMonth);
		}
	}, [month]);

	useEffect(() => {
    if (year && month) {
			updateDays(year, month);
    }
	}, [year, month]);

	useEffect(() => {
		onChange({
			year,
			month,
			day,
		});
	}, [year, month, day]);

	return (
		<>
			<label htmlFor={id} className={labelClass}>
				<p>
					{label}
					{isRequired ? <span>*</span> : null}
				</p>
			</label>
			<div className={"mt-1 grid grid-cols-3 gap-x-4"}>
				<div className={"relative"}>
						<select
      				ref={yearSelectRef}
							id="year"
							name="year"
							className={
								"w-full appearance-none rounded-none border-0 border-b bg-white bg-none shadow-none focus:outline-none focus:ring-0 " +
								fieldClass +
								(isValidYear ? " border-[#a8a8a8]" : " border-[#ff4750]")
							}
							value={year ?? ""}
							onChange={(e) =>
								e.target.value === ""
									? setYear(undefined)
									: setYear(parseInt(e.target.value, 10))
							}
						>
							<option value="" disabled> </option>
							{years.map((y, index) => (
								<option key={index} value={y}>
									{y}
								</option>
							))}
						</select>
						{!year ? 
							<div
								style={{pointerEvents: 'none', height: 'calc(100% - 2px)'}}
								className={"absolute top-0 left-0 bg-white w-full " + fieldClass}
								onClick={() => yearSelectRef.current.focus()}
							>
							</div>
							: null
						}
					{isValidYear ? (
						<img
							alt={"dropdown-button"}
							className={downImgClass}
							src={window.assetUrl("/images/liff/triangle.png")}
						/>
					) : (
						<img
							alt={"dropdown-button"}
							className={downImgClass}
							src={window.assetUrl("/images/liff/triangle-red.png")}
						/>
					)}
					<p
						className={
							(isValidYear ? "text-transparent" : "text-[#A6A6A6]") + " text-[10px] absolute whitespace-nowrap bottom-0 translate-y-full"
						}
					>
						{errorMessage}
					</p>
				</div>
				<div className={"relative"}>
					<select
						ref={monthSelectRef}
						id="month"
						name="month"
						className={
							"w-full appearance-none rounded-none border-0 border-b bg-white bg-none shadow-none focus:outline-none focus:ring-0 " +
							fieldClass +
							(isValidMonth ? " border-[#a8a8a8]" : " border-[#ff4750]")
						}
						value={month ?? ""}
						onChange={(e) =>
							e.target.value === ""
								? setMonth(undefined)
								: setMonth(parseInt(e.target.value, 10))
						}
					>
						<option value="" disabled> </option>
						{months.map((y, index) => (
							<option key={index} value={y}>
								{y}
							</option>
						))}
					</select>
					{!month ? 
						<div
							style={{pointerEvents: 'none', height: 'calc(100% - 2px)'}}
							className={"absolute top-0 left-0 bg-white w-full " + fieldClass}
							onClick={() => monthSelectRef.current.focus()}
						>
						</div>
						: null
					}
					{isValidMonth ? (
						<img
							alt={"dropdown-button"}
							className={downImgClass}
							src={window.assetUrl("/images/liff/triangle.png")}
						/>
					) : (
						<img
							alt={"dropdown-button"}
							className={downImgClass}
							src={window.assetUrl("/images/liff/triangle-red.png")}
						/>
					)}
					<p
						className={
							(isValidMonth ? "text-transparent" : "text-[#A6A6A6]") + " text-[10px] absolute whitespace-nowrap bottom-0 translate-y-full"
						}
					>
						{errorMessage}
					</p>
				</div>
				<div className={"relative"}>
					<select
						ref={daySelectRef}
						id="day"
						name="day"
						className={
							"w-full appearance-none rounded-none border-0 border-b bg-white bg-none shadow-none focus:outline-none focus:ring-0 " +
							fieldClass +
							(isValidDay ? " border-[#a8a8a8]" : " border-[#ff4750]")
						}
						value={day ?? ""}
						onChange={(e) =>
							e.target.value === ""
								? setDay(undefined)
								: setDay(parseInt(e.target.value, 10))
						}
						onClick={()=>handleDateDropdownClick()}
					>
						<option value="" disabled> </option>
						{days.map((y, index) => (
							<option key={index} value={y}>
								{y}
							</option>
						))}
					</select>
					{!day ? 
						<div
							style={{pointerEvents: 'none', height: 'calc(100% - 2px)'}}
							className={"absolute top-0 left-0 bg-white w-full " + fieldClass}
							onClick={() => daySelectRef.current.focus()}
						>
						</div>
						: null
					}
					{isValidDay ? (
						<img
							alt={"dropdown-button"}
							className={downImgClass}
							src={window.assetUrl("/images/liff/triangle.png")}
						/>
					) : (
						<img
							alt={"dropdown-button"}
							className={downImgClass}
							src={window.assetUrl("/images/liff/triangle-red.png")}
						/>
					)}
					<p
						className={
							(isValidDay ? "text-transparent" : "text-[#A6A6A6]") + " text-[10px] absolute whitespace-nowrap bottom-0 translate-y-full"
						}
					>
						{errorMessage}
					</p>
				</div>
			</div>
			<p
				className={
					(isValid ? "text-transparent" : "text-[#A6A6A6]") + " text-[10px] absolute whitespace-nowrap bottom-0 translate-y-full"
				}
			>
				{errorMessage}
			</p>
		</>
	);
};
